/** @prettier */

import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useLoaderData, useParams} from 'react-router-dom';
import {Grid, Card, CardContent, Box, Stack} from '@mui/material';

//MTH component import
import OtherAddDialog from './OtherAddDialog';
import OtherEditDialog from './OtherEditDialog';
import OtherDeleteDialog from './OtherDeleteDialog';
import CardContents from '../../component/CardContents';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import GWConfigUpdateDrawer from '../gw-registration/GWConfigUpdateDrawer';
//API import
import {
  getCompositionsGateway,
  getCompositionsSLIFUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack} from '../../define';
import {useOutlet} from '../gw-registration/GWMain';
import {gwInfoSort, sortByUnicode} from '../../functions/sort';
import {checkUnitExistenceByGatewayID} from '../../functions/checkUnitExistence';

type LoaderReturnType = {
  /** サイトに登録されているゲートウェイの情報 */
  allGWInfo: apiIF.res.getCompositionsGateway;
  /** 表示しているゲートウェイが課金しているかどうか */
  isBilling: boolean;
  /** 表示しているゲートウェイに登録されているその他空調機の情報 */
  slOtherInfo: apiIF.res.getCompositionsSLIFUnit;
  /** 登録ユニットの有無 */
  hasUnit: boolean;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined || params.gwID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //ゲートウェイ情報の取得
    promises.push(getCompositionsGateway(params.siteID, {'max-result': 100}));
    //その他空調機の情報を取得
    promises.push(
      getCompositionsSLIFUnit(params.siteID, params.gwID, {
        'max-result': 400,
      })
    );
    // ゲートウェイの機器登録状況を取得
    promises.push(checkUnitExistenceByGatewayID(params.siteID, params.gwID));
    const response = await Promise.all(promises);

    const allGWInfo = response[0] as apiIF.res.getCompositionsGateway;
    const slOtherInfo = response[1] as apiIF.res.getCompositionsSLIFUnit;
    /** ユニットの登録状況 */
    const unitExistence = response[2] as boolean;

    //課金状況の判定
    let isBilling = false;
    const displayGWInfo = allGWInfo['gateway-informations'].find(gwInfo => {
      return gwInfo['gateway-id'] === params.gwID;
    });
    if (
      displayGWInfo !== undefined &&
      displayGWInfo['billing-status'] !== undefined
    ) {
      isBilling = displayGWInfo['billing-status'] !== 'unpaid';
    }

    //表示内容をUnicode順に並び替え
    slOtherInfo['unit-informations'].sort(sortByUnicode('unit-name'));
    return {
      allGWInfo: gwInfoSort(allGWInfo),
      isBilling: isBilling,
      slOtherInfo: slOtherInfo,
      hasUnit: unitExistence as boolean,
    };
  } catch {
    return undefined;
  }
}

const OtherRegistration = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  //パスパラメータ取得
  const {siteID, gwID, systemType: slSystem} = useParams();
  //親コンポーネントから値を取得
  const {setAllGWInfo} = useOutlet();

  const [isBilling, setIsBilling] = useState(false); //表示しているゲートウェイが課金しているかどうか
  //prettier-ignore
  const [slOtherInfo, setSLOtherInfo] = useState<apiIF.res.getCompositionsSLIFUnit | null>(null); //その他空調機の情報
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [selectedUnitID, setSelectedUnitID] = useState(''); //選択した空調機のID
  const [hasConfigGW, setHasConfigGW] = useState<boolean>(false); //GW毎の構成情報未反映の有無
  const [hasUnit, setHasUnit] = useState<boolean>(false); //GWの機器登録有無
  const [slType, setSLType] = useState<'new' | 'old'>('new'); //SL新旧

  /** 空調機追加ボタンの名前 */
  const addButtonName = trans('OtherAddDialog.buttonTxt.addInterface');

  //インタフェースタイプ変換テーブル
  const ifTypeTable: {[key: string]: string} = {
    'SC-ADNA': trans('OtherRegistration.item.value.interfaceType.scAdnaE'),
    'other': trans('OtherRegistration.item.value.interfaceType.other'),
    '': trans('OtherRegistration.item.value.interfaceType.none'),
  };
  /** 電源仕様変換テーブル */
  const powerSupplyTable: {[key: string]: string} = {
    singlePhase: trans('OtherRegistration.item.value.powerSupply.singlePhase'),
    threePhase: trans('OtherRegistration.item.value.powerSupply.threePhase'),
  };

  //その他空調機の情報を取得
  useEffect(() => {
    if (loaderData !== undefined) {
      setAllGWInfo(loaderData['allGWInfo']);
      setIsBilling(loaderData.isBilling);
      setSLOtherInfo(loaderData['slOtherInfo']);
      setHasUnit(loaderData.hasUnit);

      //構成情報未反映の有無・SL新旧をチェック
      for (const gwInfo of loaderData['allGWInfo']['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        setHasConfigGW(
          loaderData.isBilling &&
            gwInfo['composition-receipt-version'] !== undefined &&
            gwInfo['composition-receipt-version'] !== 'latest'
        );
        if (gwInfo['sl-type'] !== undefined && gwInfo['sl-type'] !== '') {
          setSLType(gwInfo['sl-type']);
        }
        break;
      }
    }
  }, [loaderData, gwID, setAllGWInfo]);

  //空調機の情報が更新された場合、ツリービューを更新
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    //ゲートウェイの情報を取得
    getCompositionsGateway(siteID, {'max-result': 100}).then(response => {
      //表示内容をUnicode順に並び替え
      response = gwInfoSort(response);
      setAllGWInfo(response);
      //ユニットの有無をチェック
      for (const gwInfo of response['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        checkUnitExistenceByGatewayID(siteID, gwID).then(response => {
          setHasUnit(response as boolean);
        });
        break;
      }
    });
  }, [slOtherInfo, siteID, setAllGWInfo]);

  /**
   * Editボタン押下時処理
   * @param slOtherID 空調機ID
   */
  const openEditDialog = (slOtherID: string) => {
    setIsOpenEditDialog(true);
    setSelectedUnitID(slOtherID);
  };
  /**
   * Deleteボタン押下時処理
   * @param slOtherID 空調機ID
   */
  const openDeleteDialog = (slOtherID: string) => {
    setIsOpenDeleteDialog(true);
    setSelectedUnitID(slOtherID);
  };

  /**
   * 歯車メニューリスト
   * @param slOtherID 空調機ID
   * @returns メニュー一覧
   */
  const gearMenuItem = (slOtherID: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        label: trans('OtherRegistration.menuItem.edit'),
        onClick: () => openEditDialog(slOtherID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `OtherRegistration.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
      {
        label: trans('OtherRegistration.menuItem.delete'),
        onClick: () => openDeleteDialog(slOtherID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.DELETE.src,
          alt: trans(
            `OtherRegistration.image.alternativeText.${iconBlack.DELETE.alt}`
          ),
        },
      },
    ];
  };

  /**
   *空調機タイプアイコン
   * @param props props
   * - iuType 空調機タイプ
   * - class 適用するクラス
   * @returns　JSX
   */
  const UnitIcon = (props: {
    unitType: apiIF.res.getCompositionsSLIFUnit['unit-informations'][0]['unit-type'];
  }) => {
    switch (props.unitType) {
      case 'AC':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.AC_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.AC_ON.alt}`
            )}`}
          />
        );
      case 'FDT':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.FDT_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.FDT_ON.alt}`
            )}`}
          />
        );
      case 'FDTS':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.FDTS_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.FDTS_ON.alt}`
            )}`}
          />
        );
      case 'FDTW':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.FDTW_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.FDTW_ON.alt}`
            )}`}
          />
        );
      case 'FDE':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.FDE_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.FDE_ON.alt}`
            )}`}
          />
        );
      case 'FDF':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.FDF_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.FDF_ON.alt}`
            )}`}
          />
        );
      case 'FDFW':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.FDFW_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.FDFW_ON.alt}`
            )}`}
          />
        );
      case 'FDK':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.FDK_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.FDK_ON.alt}`
            )}`}
          />
        );
      case 'FDU':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.FDU_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.FDU_ON.alt}`
            )}`}
          />
        );
      case 'VENT':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.VENTILATOR_UNIT_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.VENTILATOR_UNIT_ON.alt}`
            )}`}
          />
        );
      case 'LIGHT':
        return (
          <img
            className={'gwSetting__contents__contents__unitTypeIcon__image'}
            src={iconBlack.LIGHT_UNIT_ON.src}
            alt={`${trans(
              `SLIUUnitTypeSelectBox.alternativeText.${iconBlack.LIGHT_UNIT_ON.alt}`
            )}`}
          />
        );
      default:
        return <></>;
    }
  };

  //空調機の情報を取得できない場合
  //パスパラメータを取得できない場合
  if (slOtherInfo == null || slSystem === undefined) {
    return <></>;
  }
  return (
    <>
      <Box className="gwSetting__title">{trans('OtherRegistration.title')}</Box>
      <Stack spacing={4}>
        {/* 空調機追加ボタン */}
        <Box className="gwSetting__contents--unit gwSetting__contents__button--add">
          <OtherAddDialog
            addButtonName={addButtonName}
            slOtherInfo={slOtherInfo}
            setSLOtherInfo={setSLOtherInfo}
            isBilling={isBilling}
            setHasConfigGW={setHasConfigGW}
            slType={slType}
          />
        </Box>
        {/* 登録済空調機が無い場合、追加ボタンのみ表示 */}
        {slOtherInfo['unit-informations'].some(
          slOther => slOther['sl-system'] === slSystem.slice(2)
        ) === false ? (
          <Card className="gwSetting__contents--unit">
            <CardContent className="gwSetting__contents__noRegister">
              {trans('OtherRegistration.instruction', {name: addButtonName})}
            </CardContent>
          </Card>
        ) : (
          //登録済その他空調機
          slOtherInfo['unit-informations']
            //一致するSL系統のみ表示する
            .filter(unitInfo => unitInfo['sl-system'] === slSystem.slice(2))
            .map(unitInfo => (
              <Card
                id={`mv-boxIf${unitInfo['unit-id']}`}
                key={unitInfo['unit-id']}
                className="gwSetting__contents--unit">
                <CardContent>
                  <Stack spacing={2}>
                    {/* 空調機名称と歯車 */}
                    <Grid
                      container
                      justifyContent="space-between"
                      className="gwSetting__contents__title">
                      {/* 空調機名称 */}
                      <Grid item xs="auto">
                        {unitInfo['unit-name']}
                      </Grid>
                      {/* 歯車ボタン */}
                      <Grid item xs={1}>
                        <GearMenu
                          id={`mv-btnIFSettingOpen${unitInfo['unit-id']}`}
                          menuItem={gearMenuItem(unitInfo['unit-id'])}
                        />
                        {/* 選択した空調機のダイアログのみ出す */}
                        {unitInfo['unit-id'] === selectedUnitID &&
                        unitInfo['unit-name'] !== undefined ? (
                          <>
                            <OtherEditDialog
                              unitID={unitInfo['unit-id']}
                              displayUnitInfo={unitInfo}
                              setUnitInfo={setSLOtherInfo}
                              isOpenDialog={isOpenEditDialog}
                              setIsOpenDialog={setIsOpenEditDialog}
                              isBilling={isBilling}
                              setHasConfigGW={setHasConfigGW}
                            />
                            <OtherDeleteDialog
                              slOtherID={unitInfo['unit-id']}
                              slOtherName={unitInfo['unit-name']}
                              setSLOtherInfo={setSLOtherInfo}
                              isOpenDialog={isOpenDeleteDialog}
                              setIsOpenDialog={setIsOpenDeleteDialog}
                              isBilling={isBilling}
                              setHasConfigGW={setHasConfigGW}
                            />
                          </>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                    {/* 空調機の情報 */}
                    <Stack
                      spacing={2}
                      className="gwSetting__contents__contents">
                      {/* SLアドレス */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'OtherRegistration.item.title.superlinkAddress'
                        )}
                        value={
                          unitInfo['sl-address'] === undefined
                            ? ''
                            : unitInfo['sl-address']
                        }
                      />
                      {/* 空調機タイプ */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'OtherRegistration.item.title.unitType'
                        )}
                        value={
                          unitInfo['unit-type'] === undefined ? (
                            ''
                          ) : (
                            <>
                              <UnitIcon unitType={unitInfo['unit-type']} />
                              <p className="gwSetting__contents__contents__unitTypeIcon__text">
                                {trans(
                                  `OtherRegistration.item.value.unitType.${unitInfo[
                                    'unit-type'
                                  ].toLowerCase()}`
                                )}
                              </p>
                            </>
                          )
                        }
                      />
                      {/* インターフェイスタイプ */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'OtherRegistration.item.title.interfaceType'
                        )}
                        value={
                          unitInfo['interface-type'] === undefined
                            ? ''
                            : ifTypeTable[unitInfo['interface-type']]
                        }
                      />
                      {/* 入力電圧 */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'OtherRegistration.item.title.voltage'
                        )}
                        value={
                          unitInfo['input-voltage'] === undefined
                            ? ''
                            : unitInfo['input-voltage']
                        }
                      />
                      {/* 電源の種類 */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'OtherRegistration.item.title.powerSupply'
                        )}
                        value={
                          unitInfo['power-supply'] === undefined
                            ? ''
                            : powerSupplyTable[unitInfo['power-supply']]
                        }
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
        )}
      </Stack>
      {/* 構成情報反映ボタン */}
      <GWConfigUpdateDrawer
        hasConfigGW={hasConfigGW}
        setHasConfigGW={setHasConfigGW}
        hasUnit={hasUnit}
      />
    </>
  );
};
export default OtherRegistration;
